import { PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/errors';
import {
  ProjectMemberRole,
  addProjectMember,
  leaveProject,
  listProjectMembers,
  projectMemberRoleNames,
  rejoinProject,
  updateProjectMember,
} from '../../api/projects';
import type { ProjectMember, ProjectMemberRoleValue } from '../../api/projects';
import { listUserAccounts } from '../../api/userAccounts';
import type { UserAccount } from '../../api/userAccounts';
import { formatDate } from './projectDisplay';

/** Giá trị của biểu mẫu thêm thành viên. */
interface MemberFormValues {
  readonly userAccountId: string;
  readonly role: ProjectMemberRoleValue;
  readonly allocationPercent: number | null;
}

/** Số bản ghi nạp cho ô chọn người dùng. */
const lookupPageSize = 200;

/**
 * Nạp danh mục tài khoản để tra tên người, và **không bao giờ ném lỗi**.
 *
 * Tên người là thứ có cũng được: danh mục tài khoản đòi quyền `PLT.user_account.view`, khác với
 * quyền xem thành viên dự án. Người có quyền xem dự án mà không có quyền đó vẫn phải đọc được
 * danh sách thành viên — khi ấy cột Người hiện định danh máy, kém đẹp nhưng vẫn phân biệt được
 * từng dòng. Để lỗi này lan ra ngoài sẽ làm cả thẻ thành viên báo lỗi và không hiện gì cả, tức
 * là mất luôn phần thông tin mà họ có quyền xem.
 */
async function loadUserNames(): Promise<readonly UserAccount[]> {
  try {
    const page = await listUserAccounts({ pageSize: lookupPageSize });

    return page.items;
  } catch {
    return [];
  }
}

/** Các lựa chọn vai trò trong dự án. */
const roleOptions = Object.entries(projectMemberRoleNames).map(([value, label]) => ({
  value: Number(value) as ProjectMemberRoleValue,
  label,
}));

/**
 * Thẻ danh sách thành viên dự án.
 *
 * <b>Hiện cả người đã rời dự án</b>, phân biệt bằng nhãn. Danh sách này trả lời câu hỏi <i>ai đã
 * từng làm dự án này</i> — câu hỏi hay được đặt ra nhất khi hệ thống hỏng sau một năm và cần tìm
 * người biết cấu hình. Lọc bỏ họ đi sẽ làm mất đúng thông tin đó.
 *
 * <b>Không có nút xóa dòng.</b> Thứ thay thế là ghi nhận rời dự án kèm ngày, và người quản lý dự án
 * hiện tại thì không rời được — phải bàn giao trước.
 */
export function ProjectMemberCard({
  projectId,
  canView,
  canManage,
}: {
  readonly projectId: string;
  readonly canView: boolean;
  readonly canManage: boolean;
}): ReactNode {
  const [form] = Form.useForm<MemberFormValues>();

  const [members, setMembers] = useState<readonly ProjectMember[] | null>(null);
  const [users, setUsers] = useState<readonly UserAccount[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!canView) {
      return;
    }

    let isCurrent = true;

    async function load(): Promise<void> {
      setErrorMessage(null);

      try {
        /*
          Nạp tên người NGAY TỪ ĐẦU, cùng lúc với danh sách thành viên.

          Trước đây danh mục tài khoản chỉ được nạp khi mở hộp thoại "Thêm người", nên bảng vẽ
          lần đầu với danh mục còn rỗng và cột Người hiện định danh máy thay vì tên. Lỗi đó xảy
          ra với mọi người dùng, nhưng dễ bị bỏ qua vì ai từng bấm nút Thêm trong cùng lượt xem
          thì thấy tên hiện ra đúng — và tưởng mọi thứ bình thường.
        */
        const [loadedMembers, loadedUsers] = await Promise.all([
          listProjectMembers(projectId),
          loadUserNames(),
        ]);

        if (isCurrent) {
          setMembers(loadedMembers);
          setUsers(loadedUsers);
        }
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được danh sách thành viên.',
        );
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [projectId, canView, reloadToken]);

  const reload = useCallback(() => { setReloadToken((token) => token + 1); }, []);

  async function openAdd(): Promise<void> {
    form.resetFields();
    form.setFieldsValue({ role: ProjectMemberRole.VisionEngineer });
    setIsFormOpen(true);

    try {
      const page = await listUserAccounts({ pageSize: lookupPageSize });

      setUsers(page.items);
    } catch (error) {
      message.error(
        error instanceof ApiError ? error.message : 'Không tải được danh sách người dùng.',
      );
    }
  }

  async function submit(values: MemberFormValues): Promise<void> {
    setIsSaving(true);

    try {
      await addProjectMember(projectId, {
        userAccountId: values.userAccountId,
        role: values.role,
        joinedAt: null,
        allocationPercent: values.allocationPercent ?? null,
      });

      message.success('Đã thêm vào dự án.');
      setIsFormOpen(false);
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không thêm được thành viên.');
    } finally {
      setIsSaving(false);
    }
  }

  async function changeRole(
    member: ProjectMember,
    role: ProjectMemberRoleValue,
  ): Promise<void> {
    try {
      await updateProjectMember(projectId, member.id, {
        role,
        allocationPercent: member.allocationPercent,
      });

      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không đổi được vai trò.');
    }
  }

  async function changeAllocation(
    member: ProjectMember,
    allocationPercent: number | null,
  ): Promise<void> {
    try {
      await updateProjectMember(projectId, member.id, {
        role: member.role,
        allocationPercent,
      });

      reload();
    } catch (error) {
      message.error(
        error instanceof ApiError ? error.message : 'Không đổi được phần trăm thời gian.',
      );
    }
  }

  async function toggleMembership(member: ProjectMember): Promise<void> {
    try {
      if (member.isActive) {
        await leaveProject(projectId, member.id, null);
        message.success('Đã ghi nhận rời dự án.');
      } else {
        await rejoinProject(projectId, member.id, null);
        message.success('Đã cho tham gia lại.');
      }

      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không thực hiện được.');
    }
  }

  /** Tên hiển thị của một người dùng, hoặc chính định danh khi chưa nạp được danh sách. */
  function displayName(userAccountId: string): string {
    const user = users.find((candidate) => candidate.id === userAccountId);

    return user === undefined ? userAccountId : `${user.fullName} (${user.userName})`;
  }

  const columns: TableColumnsType<ProjectMember> = [
    {
      title: 'Người',
      dataIndex: 'userAccountId',
      render: (userAccountId: string, member) => (
        <Space size={4}>
          <span>{displayName(userAccountId)}</span>
          {member.isProjectManager ? <Tag color="blue">Quản lý dự án</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Vai trò',
      dataIndex: 'role',
      width: 200,
      render: (role: ProjectMemberRoleValue, member) =>
        canManage && member.isActive ? (
          <Select<ProjectMemberRoleValue>
            size="small"
            value={role}
            style={{ width: 180 }}
            options={roleOptions}
            onChange={(value) => void changeRole(member, value)}
          />
        ) : (
          projectMemberRoleNames[role]
        ),
    },
    {
      title: 'Thời gian (%)',
      dataIndex: 'allocationPercent',
      width: 130,
      render: (allocationPercent: number | null, member) =>
        canManage && member.isActive ? (
          <InputNumber<number>
            size="small"
            min={1}
            max={100}
            style={{ width: 90 }}
            value={allocationPercent}
            onBlur={(event) => {
              const raw = event.target.value.trim();
              const parsed = raw === '' ? null : Number(raw);

              if (parsed !== member.allocationPercent) {
                void changeAllocation(member, parsed);
              }
            }}
          />
        ) : (
          (allocationPercent ?? '—')
        ),
    },
    {
      title: 'Tham gia',
      dataIndex: 'joinedAt',
      width: 120,
      render: (joinedAt: string) => formatDate(joinedAt),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      width: 170,
      render: (isActive: boolean, member) =>
        isActive ? (
          <Tag color="success">Đang tham gia</Tag>
        ) : (
          <Tooltip title="Vẫn đọc được dự án ở chế độ chỉ đọc, nhưng không nhận thông báo và không được phân việc mới.">
            <Tag>Đã rời {formatDate(member.leftAt)}</Tag>
          </Tooltip>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      render: (_, member) => (
        <Tooltip
          title={
            member.isProjectManager && member.isActive
              ? 'Người quản lý dự án phải được bàn giao trước khi rời khỏi danh sách.'
              : ''
          }
        >
          <Button
            size="small"
            disabled={!canManage || (member.isProjectManager && member.isActive)}
            onClick={() => void toggleMembership(member)}
          >
            {member.isActive ? 'Rời dự án' : 'Tham gia lại'}
          </Button>
        </Tooltip>
      ),
    },
  ];

  if (!canView) {
    return (
      <Card size="small" title="Thành viên dự án">
        <Alert type="info" showIcon message="Bạn không có quyền xem danh sách thành viên." />
      </Card>
    );
  }

  return (
    <Card
      size="small"
      title="Thành viên dự án"
      extra={
        <Button
          size="small"
          type="primary"
          icon={<PlusOutlined />}
          disabled={!canManage}
          onClick={() => void openAdd()}
        >
          Thêm người
        </Button>
      }
    >
      {errorMessage !== null ? (
        <Alert type="error" showIcon message={errorMessage} style={{ marginBottom: 12 }} />
      ) : null}

      <Table<ProjectMember>
        rowKey="id"
        size="small"
        pagination={false}
        columns={columns}
        dataSource={members as ProjectMember[] | undefined}
        loading={members === null && errorMessage === null}
      />

      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        Thêm một người vào dự án là cho họ quyền đọc toàn bộ dự án đó.
      </Typography.Text>

      <Modal
        open={isFormOpen}
        title="Thêm người vào dự án"
        okText="Thêm"
        cancelText="Đóng"
        confirmLoading={isSaving}
        onCancel={() => { setIsFormOpen(false); }}
        onOk={() => { form.submit(); }}
      >
        <Form form={form} layout="vertical" onFinish={(values) => void submit(values)}>
          <Form.Item
            name="userAccountId"
            label="Người dùng"
            rules={[{ required: true, message: 'Chưa chọn người.' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Chọn người"
              options={users.map((user) => ({
                value: user.id,
                label: `${user.fullName} (${user.userName})`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="role"
            label="Vai trò trong dự án"
            rules={[{ required: true, message: 'Chưa chọn vai trò.' }]}
          >
            <Select<ProjectMemberRoleValue> options={roleOptions} />
          </Form.Item>
          <Form.Item
            name="allocationPercent"
            label="Phần trăm thời gian"
            extra="Để trống nếu chưa xác định. Cộng trên nhiều dự án là cách biết ai đang quá tải."
          >
            <InputNumber<number> min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
